import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-styling-options';
import {ItemSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-size-option';
import {TextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-color-option';
import {ItemAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-alignment-option';
import {TextVerticalAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-vertical-alignment-option';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {TextItemPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-text-options/poster-editing-text-options.types';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ItemLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-letter-spacing-option';
import {ItemLineHeightOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-line-height-option';
import {ItemFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-font-option';
import {TextFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-fill-color-panel';
import {TextOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-option';
import {TextOutlineColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-color-panel';
import {TextListOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-list-option';
import {ItemBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-background-option';
import {ItemBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-background-color-panel';
import {TextItemFontSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-font-selector';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {TextItemListColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-list-color-panel';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {LanguageNotSupportedByTextFontOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/language-not-supported-by-text-font-option';
import {EditTextOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/edit-text-option';
import {isMobile} from 'react-device-detect';
import {isTouchDevice} from '@Utils/browser.util';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useIsOpenedTextPanelValid} from '@Hooks/poster-editor/useIsOpenedTextPanelValid';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingTextOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(TextItemPanels.NONE);
  useIsOpenedTextPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (activeItem === null || !activeItem) {
    throw new Error('Active item is undefined in poster editing text options');
  }

  if (!activeItem?.isText()) {
    throw new Error(`Expected a text item - got ${activeItem?.gitype} instead`);
  }

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === TextItemPanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      if (currentlyOpenedPanel === TextItemPanels.FONT) {
        return null;
      }
      return (
        <ContextualMenuHeader id="sticker-contextual-menu-header">
          <ContextualMenuTextHeader
            text={getHeaderText()}
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }
              setCurrentlyOpenedPanel(TextItemPanels.NONE);
            }}
          />
        </ContextualMenuHeader>
      );
    }

    return null;
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.ANIMATION);
          }}
        />
        {isMobile || isTouchDevice() ? <EditTextOption isInSidebar /> : null}
        <ContextualMenuHeadingOption id="text-styles-heading-option" text={window.i18next.t('pmwjs_styles')} />
        <ItemFontOption
          onClickFontButton={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.FONT);
          }}
        />
        <LanguageNotSupportedByTextFontOption />
        <ItemStylingOptions />
        <ItemSizeOption />
        <TextColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.FILL);
          }}
        />
        <TextOutlineOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.OUTLINE);
          }}
        />
        <ItemAlignmentOption />
        <TextVerticalAlignmentOption />
        <TextListOption
          onClickColorObject={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.LIST_COLOR);
          }}
        />
        <ItemLineHeightOption />
        <ItemLetterSpacingOption />
        <ItemBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.BACKGROUND_COLOR);
          }}
        />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TextItemPanels.CUSTOM_SHADOW);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== TextItemPanels.NONE;
  };

  const getSubPanelOptions = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case TextItemPanels.CUSTOM_SHADOW:
        return <CustomShadowColorPanel />;
      case TextItemPanels.FILL:
        return <TextFillColorPanel />;
      case TextItemPanels.OUTLINE:
        return <TextOutlineColorPanel />;
      case TextItemPanels.BACKGROUND_COLOR:
        return <ItemBackgroundColorPanel />;
      case TextItemPanels.ANIMATION:
        return <ItemAnimationPanel />;
      case TextItemPanels.FONT:
        return (
          <TextItemFontSelector
            onBack={(): void => {
              setCurrentlyOpenedPanel(TextItemPanels.NONE);
            }}
          />
        );
      case TextItemPanels.LIST_COLOR:
        return <TextItemListColorPanel />;
      default:
        throw new Error(`unhandled case ${currentlyOpenedPanel} for sub panels in text item`);
    }
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanelOptions();
    }

    return getDefaultOptions();
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.TEXT_DEFAULT_POSITION : ContextualMenuContentKeys.TEXT_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu
        id="image-contextual-options"
        contentClassName={currentlyOpenedPanel === TextItemPanels.FONT ? 'spacing-p-0' : ''}
        includeLeftRightPadding={!isSubPanelOpened()}
        header={getHeader()}
        contentKey={getKey()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
